import { Suspense, useState, useMemo, useRef } from "react";
import { Await, useLoaderData } from "react-router-dom";
import { ClimbingBoxLoader } from "react-spinners";
import { VegaEmbed } from "react-vega";
import { VEGA_CONFIG, VEGA_HEIGHT, VEGA_REGRESSION_COLOR, VEGA_WIDTH, secondsAsPace } from "../vegahelpers";

interface PaceStats {
    avgPace: number | null;
    bestPace: number | null;
    bestDate: string | null;
}

function computeStats(tableData: any[], view: any): PaceStats {
    let filtered = tableData.filter((w: any) => w.distance > 0 && w.time > 0);

    try {
        const brushValue = view.signal('timeBrush');
        if (brushValue?.yearweek_date && Array.isArray(brushValue.yearweek_date)) {
            const [startDate, endDate] = brushValue.yearweek_date;
            filtered = filtered.filter((w: any) => {
                const d = new Date(w.date).getTime();
                return d >= startDate && d <= endDate;
            });
        }
    } catch (_) {}

    try {
        const typesValue = view.signal('selectedTypes');
        if (typesValue?.comments && Array.isArray(typesValue.comments) && typesValue.comments.length > 0) {
            const selected = new Set<string>(typesValue.comments);
            filtered = filtered.filter((w: any) => selected.has(w.comments));
        }
    } catch (_) {}

    const paces = filtered
        .map((w: any) => ({ pace: w.time * 50 / w.distance, date: w.date }))
        .filter((w: any) => isFinite(w.pace) && w.pace > 0);

    if (paces.length === 0) return { avgPace: null, bestPace: null, bestDate: null };

    const avgPace = paces.reduce((sum: number, w: any) => sum + w.pace, 0) / paces.length;
    const best = paces.reduce((min: any, w: any) => w.pace < min.pace ? w : min);
    return { avgPace, bestPace: best.pace, bestDate: best.date };
}

function PaceOverTimeChart({ tableData, metadata }: { tableData: any[], metadata: any }) {
    const [stats, setStats] = useState<PaceStats>({ avgPace: null, bestPace: null, bestDate: null });
    const [showAvgLine, setShowAvgLine] = useState(false);
    const viewRef = useRef<any>(null);

    const spec = useMemo(() => ({
        config: VEGA_CONFIG,
        params: [{ "name": "showAvgLine", "value": false }],
        vconcat: [{
            width: VEGA_WIDTH,
            height: VEGA_HEIGHT,
            transform: [{ calculate: "datum.time * 50 / datum.distance", as: "Average Pace" }],
            encoding: {},
            layer: [
                {
                    params: [
                        {
                            "name": "selectedTypes",
                            "select": { "type": "point", "fields": ["comments"] },
                            "bind": "legend"
                        }
                    ],
                    mark: 'circle',
                    title: {
                        text: "Pace per Workout over Time",
                        subtitle: "Shows how your pace has improved"
                    },
                    encoding: {
                        x: { field: 'date', type: 'temporal', axis: { title: "Date of Workout" }, scale: { domain: { param: "timeBrush" } } },
                        y: { field: 'Average Pace', type: 'quantitative', sort: "descending", axis: { formatType: 'secondsAsPace' }, scale: { domainMin: metadata.paceAxisMin, domainMax: metadata.paceAxisMax } },
                        color: { field: "comments" },
                        tooltip: [
                            { field: "Average Pace", type: "quantitative", formatType: "secondsAsPace" },
                            { field: "date", type: "temporal" },
                            { field: "distance", type: "quantitative" },
                            { field: "comments" }
                        ],
                        opacity: {
                            condition: { "param": "selectedTypes", "value": 1 },
                            value: 0.2
                        }
                    },
                },
                {
                    mark: { type: "line", color: VEGA_REGRESSION_COLOR },
                    transform: [
                        { filter: "!showAvgLine" },
                        { filter: { param: "selectedTypes" } },
                        { filter: { param: "timeBrush" } },
                        { regression: "Average Pace", on: "date" }
                    ],
                    encoding: {
                        x: { field: 'date', type: 'temporal', scale: { domain: { param: "timeBrush" } } },
                        y: { field: 'Average Pace', type: 'quantitative', sort: "descending" },
                    }
                },
                {
                    mark: { type: "text", color: VEGA_REGRESSION_COLOR, x: "width", y: -5, align: "right" },
                    transform: [
                        { filter: "!showAvgLine" },
                        { filter: { param: "selectedTypes" } },
                        { filter: { param: "timeBrush" } },
                        { regression: "Average Pace", on: "date", params: true },
                        { calculate: "'R²: '+format(datum.rSquared, '.2f')", as: "Params" }
                    ],
                    encoding: { text: { type: "nominal", field: "Params" } }
                },
                {
                    mark: { type: "rule", color: VEGA_REGRESSION_COLOR, strokeWidth: 2, strokeDash: [6, 4] },
                    transform: [
                        { filter: "showAvgLine" },
                        { filter: { param: "selectedTypes" } },
                        { filter: { param: "timeBrush" } }
                    ],
                    encoding: {
                        y: { field: 'Average Pace', type: 'quantitative', aggregate: 'mean', axis: { title: 'Average Pace' } }
                    }
                }
            ],
        }, {
            "width": VEGA_WIDTH,
            "height": 60,
            "mark": "bar",
            "params": [{ "name": "timeBrush", "select": { "type": "interval", "encodings": ["x"] } }],
            "encoding": {
                "x": { "field": "date", "type": "temporal", timeUnit: "yearweek", axis: { title: "Week of Workout", format: "%Y-%m-%d" } },
                "y": { "aggregate": "count", "type": "quantitative", "axis": { "tickCount": 2, "grid": false, title: "Workouts" } }
            }
        }],
        data: { name: 'table' },
    }), [metadata]);

    const handleAvgLineToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setShowAvgLine(checked);
        viewRef.current?.signal('showAvgLine', checked).run();
    };

    return (
        <>
            <VegaEmbed
                spec={spec}
                onEmbed={(result) => {
                    viewRef.current = result.view;
                    result.view.data('table', tableData).run();
                    const update = () => setStats(computeStats(tableData, result.view));
                    update();
                    result.view.addSignalListener('timeBrush', update);
                    result.view.addSignalListener('selectedTypes', update);
                }}
            />
            {stats.avgPace !== null && (
                <div style={{ marginTop: '0.75rem', fontSize: '1.1rem', lineHeight: '1.8' }}>
                    <div>
                        Average pace: <b>{secondsAsPace(stats.avgPace)}</b>
                        <label style={{ cursor: 'pointer', marginLeft: '1.5rem' }}>
                            Show reference line: <input type="checkbox" checked={showAvgLine} onChange={handleAvgLineToggle} style={{ marginLeft: '0.4rem' }} />
                        </label>
                    </div>
                    <div>Best pace: <b>{secondsAsPace(stats.bestPace!)}</b>
                        {stats.bestDate && <> ({new Date(stats.bestDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})</>}
                    </div>
                </div>
            )}
        </>
    );
}

export default function PaceOverTime() {
    const data: any = useLoaderData();
    return (
        <div>
            <Suspense fallback={
                <div>
                    <h1>Loading workout data...</h1>
                    <ClimbingBoxLoader color="#574f6e" />
                </div>
            }>
                <Await resolve={Promise.all([data.data, data.metadata])}>
                    {([tableData, metadata]) => (
                        <PaceOverTimeChart tableData={tableData} metadata={metadata} />
                    )}
                </Await>
            </Suspense>
            <div><b>Tip: select a region on the lower graph to focus on a particular time range. Click a comment in the legend to filter to specific workout types.</b></div>
        </div>
    );
}
